import React, { useState, useEffect, useCallback } from 'react';
import HomeScreen from './components/HomeScreen';
import DreamForm from './components/DreamForm';
import ChatScreen from './components/ChatScreen';
import PrivacyPolicy from './components/PrivacyPolicy';
import DictionaryView from './components/DictionaryView';
import DreamDetailView from './components/DreamDetailView';
import LetterView from './components/LetterView';
import CategoryView from './components/CategoryView';
import AppNav from './components/AppNav';
import AppPage from './components/AppPage';
import MobileStickyCta from './components/MobileStickyCta';
import { Screen, UserData } from './types';
import { findDreamBySlug, DREAM_CATEGORIES, ARABIC_LETTERS } from './data/dreamsData';
import { 
  getHomePageSeo, 
  getDictionaryPageSeo, 
  getDreamTopicSeo, 
  getLetterPageSeo, 
  getCategoryPageSeo, 
  getPrivacyPolicySeo,
  getAppPageSeo,
  PageSeoMetadata
} from './lib/seo';

interface RouteState {
  screen: Screen;
  dreamSlug?: string;
  letter?: string;
  categorySlug?: string;
  initialDreamText?: string;
}

function parseLocationPath(pathname: string): RouteState {
  let cleanPath = pathname.split('?')[0].split('#')[0];
  cleanPath = cleanPath.replace(/\/+$/, '') || '/';
  cleanPath = cleanPath.replace(/\.html$/, '');
  cleanPath = cleanPath.replace(/\/index$/, '') || '/';

  let decodedPath = cleanPath;
  try {
    decodedPath = decodeURIComponent(cleanPath);
  } catch (e) {
    decodedPath = cleanPath;
  }
  decodedPath = decodedPath.replace(/\/+$/, '') || '/';
  decodedPath = decodedPath.replace(/\.html$/, '');
  decodedPath = decodedPath.replace(/\/index$/, '') || '/';

  if (cleanPath === '' || cleanPath === '/') {
    return { screen: Screen.HOME };
  }

  if (cleanPath === '/privacy') {
    return { screen: Screen.PRIVACY };
  }

  if (cleanPath === '/app') {
    return { screen: Screen.APP };
  }

  if (cleanPath === '/dictionary' || cleanPath === '/encyclopedia') {
    return { screen: Screen.DICTIONARY };
  }

  const dreamMatch = decodedPath.match(/^\/(?:dream|tafsir)\/(.+)$/);
  if (dreamMatch) {
    const slug = dreamMatch[1].trim();
    return { screen: Screen.DREAM_DETAIL, dreamSlug: slug };
  }

  const letterMatch = decodedPath.match(/^\/letter\/(.+)$/);
  if (letterMatch) {
    const letter = letterMatch[1].trim();
    return { screen: Screen.LETTER, letter };
  }

  const categoryMatch = decodedPath.match(/^\/category\/([a-zA-Z0-9_-]+)$/);
  if (categoryMatch) {
    const categorySlug = categoryMatch[1].trim();
    return { screen: Screen.CATEGORY, categorySlug };
  }

  return { screen: Screen.HOME };
}

function extractDreamQueryParam(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('dream');
    if (raw && raw.trim().length > 0) {
      return decodeURIComponent(raw.trim());
    }
  } catch (e) {}
  return null;
}

function upsertHeadMeta(attr: 'name' | 'property', key: string, content: string) {
  if (typeof document === 'undefined') return;
  const selector = attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function applySeo(seo: PageSeoMetadata) {
  if (typeof document === 'undefined') return;
  document.title = seo.title;

  upsertHeadMeta('name', 'description', seo.description);
  upsertHeadMeta('name', 'keywords', seo.keywords.join(', '));

  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute('href', seo.canonicalUrl);

  upsertHeadMeta('name', 'robots', seo.robots || 'index, follow');

  // Open Graph
  upsertHeadMeta('property', 'og:type', seo.ogType);
  upsertHeadMeta('property', 'og:url', seo.canonicalUrl);
  upsertHeadMeta('property', 'og:title', seo.title);
  upsertHeadMeta('property', 'og:description', seo.description);
  upsertHeadMeta('property', 'og:image', seo.ogImage);
  if (seo.ogImageWidth && seo.ogImageHeight) {
    upsertHeadMeta('property', 'og:image:width', String(seo.ogImageWidth));
    upsertHeadMeta('property', 'og:image:height', String(seo.ogImageHeight));
  }

  // Twitter
  upsertHeadMeta('property', 'twitter:card', 'summary_large_image');
  upsertHeadMeta('property', 'twitter:url', seo.canonicalUrl);
  upsertHeadMeta('property', 'twitter:title', seo.title);
  upsertHeadMeta('property', 'twitter:description', seo.description);
  upsertHeadMeta('property', 'twitter:image', seo.ogImage);

  // JSON-LD Structured Data (keep in sync with SSR head)
  document.head.querySelectorAll('script[type="application/ld+json"]').forEach((s) => s.remove());
  for (const schema of seo.jsonLdSchemas) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
  }
}

const App: React.FC = () => {
  const [routeState, setRouteState] = useState<RouteState>(() => {
    if (typeof window !== 'undefined') {
      return parseLocationPath(window.location.pathname);
    }
    return { screen: Screen.HOME };
  });

  const [userData, setUserData] = useState<UserData | null>(null);
  const [initialDreamText, setInitialDreamText] = useState<string>('');

  useEffect(() => {
    const dreamQ = extractDreamQueryParam();
    if (dreamQ) {
      setInitialDreamText(dreamQ);
    }
  }, []);

  const syncSeo = useCallback((state: RouteState) => {
    if (state.screen === Screen.HOME || state.screen === Screen.FORM || state.screen === Screen.CHAT) {
      const dreamQuery = extractDreamQueryParam();
      const seo = getHomePageSeo(dreamQuery || undefined);
      applySeo(seo);
    } else if (state.screen === Screen.PRIVACY) {
      const seo = getPrivacyPolicySeo();
      applySeo(seo);
    } else if (state.screen === Screen.APP) {
      const seo = getAppPageSeo();
      applySeo(seo);
    } else if (state.screen === Screen.DICTIONARY) {
      const seo = getDictionaryPageSeo();
      applySeo(seo);
    } else if (state.screen === Screen.DREAM_DETAIL && state.dreamSlug) {
      const dream = findDreamBySlug(state.dreamSlug);
      if (dream) {
        const seo = getDreamTopicSeo(dream);
        applySeo(seo);
      }
    } else if (state.screen === Screen.LETTER && state.letter) {
      const seo = getLetterPageSeo(state.letter);
      applySeo(seo);
    } else if (state.screen === Screen.CATEGORY && state.categorySlug) {
      const seo = getCategoryPageSeo(state.categorySlug);
      applySeo(seo);
    }
  }, []);

  useEffect(() => {
    syncSeo(routeState);
  }, [routeState, syncSeo]);

  useEffect(() => {
    const handlePopState = () => {
      const parsed = parseLocationPath(window.location.pathname);
      setRouteState(parsed);
      const dreamQ = extractDreamQueryParam();
      if (dreamQ) {
        setInitialDreamText(dreamQ);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path: string, newState: RouteState) => {
    if (typeof window !== 'undefined') {
      window.history.pushState(null, '', path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setRouteState(newState);
  };

  const handleBackToHome = () => {
    setUserData(null);
    navigate('/', { screen: Screen.HOME });
  };

  const handleStartAiForm = (symbolName?: string) => {
    if (symbolName) {
      setInitialDreamText(symbolName.startsWith('رأيت') ? symbolName : `رأيت في منامي ${symbolName} و`);
    } else {
      setInitialDreamText('');
    }
    setRouteState({ screen: Screen.FORM });
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFormSubmit = (data: UserData) => {
    setUserData(data);
    setRouteState({ screen: Screen.CHAT });
  };

  const handleBackToForm = () => {
    setRouteState({ screen: Screen.FORM });
  };

  const handleNavigateToPrivacy = () => {
    navigate('/privacy', { screen: Screen.PRIVACY });
  };

  const handleNavigateToDictionary = () => {
    navigate('/dictionary', { screen: Screen.DICTIONARY });
  };

  const handleNavigateToApp = () => {
    navigate('/app', { screen: Screen.APP });
  };

  const handleSelectDream = (slug: string) => {
    navigate(`/dream/${slug}`, { screen: Screen.DREAM_DETAIL, dreamSlug: slug });
  };

  const handleSelectLetter = (letter: string) => {
    navigate(`/letter/${encodeURIComponent(letter)}`, { screen: Screen.LETTER, letter });
  };

  const handleSelectCategory = (categorySlug: string) => {
    navigate(`/category/${categorySlug}`, { screen: Screen.CATEGORY, categorySlug });
  };

  const currentDream = routeState.dreamSlug ? findDreamBySlug(routeState.dreamSlug) : null;

  return (
    <div className="min-h-screen w-full bg-[#1E293B] text-white flex flex-col font-sans">
      {routeState.screen !== Screen.FORM && routeState.screen !== Screen.CHAT && (
        <AppNav
          onNavigateToHome={handleBackToHome}
          onNavigateToDictionary={handleNavigateToDictionary}
          onNavigateToApp={handleNavigateToApp}
          onStartAiInterpretation={() => handleStartAiForm()}
        />
      )}
      <main className="flex-1 w-full relative">
        {routeState.screen === Screen.HOME && (
          <HomeScreen 
            onStart={handleStartAiForm}
            onNavigateToPrivacy={handleNavigateToPrivacy}
            onNavigateToDictionary={handleNavigateToDictionary}
            onNavigateToApp={handleNavigateToApp}
            onSelectDream={handleSelectDream}
            onSelectLetter={handleSelectLetter}
            onSelectCategory={handleSelectCategory}
          />
        )}

        {routeState.screen === Screen.DICTIONARY && (
          <DictionaryView 
            onSelectDream={handleSelectDream}
            onSelectLetter={handleSelectLetter}
            onSelectCategory={handleSelectCategory}
            onStartAiInterpretation={handleStartAiForm}
            onBackToHome={handleBackToHome}
          />
        )}

        {routeState.screen === Screen.DREAM_DETAIL && currentDream && (
          <DreamDetailView 
            dream={currentDream}
            onSelectDream={handleSelectDream}
            onSelectCategory={handleSelectCategory}
            onSelectLetter={handleSelectLetter}
            onNavigateToDictionary={handleNavigateToDictionary}
            onStartAiInterpretation={handleStartAiForm}
            onBackToHome={handleBackToHome}
          />
        )}

        {routeState.screen === Screen.LETTER && routeState.letter && (
          <LetterView 
            letter={routeState.letter}
            onSelectLetter={handleSelectLetter}
            onSelectDream={handleSelectDream}
            onNavigateToDictionary={handleNavigateToDictionary}
            onStartAiInterpretation={handleStartAiForm}
            onBackToHome={handleBackToHome}
          />
        )}

        {routeState.screen === Screen.CATEGORY && routeState.categorySlug && (
          <CategoryView 
            categorySlug={routeState.categorySlug}
            onSelectCategory={handleSelectCategory}
            onSelectLetter={handleSelectLetter}
            onSelectDream={handleSelectDream}
            onNavigateToDictionary={handleNavigateToDictionary}
            onStartAiInterpretation={handleStartAiForm}
            onBackToHome={handleBackToHome}
          />
        )}

        {routeState.screen === Screen.PRIVACY && (
          <PrivacyPolicy onBack={handleBackToHome} />
        )}

        {routeState.screen === Screen.APP && (
          <AppPage
            onBackToHome={handleBackToHome}
            onNavigateToDictionary={handleNavigateToDictionary}
            onStartAiInterpretation={(symbolName) => handleStartAiForm(symbolName)}
          />
        )}

        {routeState.screen === Screen.FORM && (
          <DreamForm 
            initialDreamText={initialDreamText}
            onSubmit={handleFormSubmit} 
            onBack={handleBackToHome} 
          />
        )}

        {routeState.screen === Screen.CHAT && userData && (
          <ChatScreen 
            userData={userData} 
            onBack={handleBackToForm} 
          />
        )}
      </main>

      {routeState.screen !== Screen.FORM && routeState.screen !== Screen.CHAT && (
        <MobileStickyCta onStartAiInterpretation={() => handleStartAiForm()} />
      )}
    </div>
  );
};

export default App;
